'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import MomentumBadge from '@/components/ui/MomentumBadge';
import FitScoreBadge from '@/components/ui/FitScoreBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import PlatformIcons from '@/components/ui/PlatformIcons';
import Icon from '@/components/ui/AppIcon';
import type { Creator } from './FindCreatorClient';

interface Props {
  creator: Creator;
  selected: boolean;
  onSelect: () => void;
  viewMode: 'grid' | 'list';
}

export default function CreatorResultCard({ creator, selected, onSelect, viewMode }: Props) {
  const [shortlisted, setShortlisted] = useState(false);

  if (viewMode === 'list') {
    return (
      <div className={`bg-card border rounded-lg p-3.5 flex items-center gap-3 transition-all ${
        selected ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/30'
      }`}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          className="w-3.5 h-3.5 accent-primary flex-shrink-0"
        />
        <div className="story-ring-primary p-0.5 rounded-full flex-shrink-0">
          <div className="w-9 h-9 rounded-full overflow-hidden border border-background">
            <AppImage src={creator.avatar} alt={creator.avatarAlt} width={36} height={36} className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-6 gap-2 items-center">
          <div className="col-span-2 sm:col-span-2">
            <p className="text-[13px] font-700 text-foreground truncate">{creator.name}</p>
            <p className="text-[11px] text-muted-foreground">{creator.handle} · {creator.city}</p>
          </div>
          <div className="hidden sm:flex items-center gap-1 flex-wrap col-span-1">
            {creator.category.slice(0, 2).map(c => (
              <span key={`list-cat-${creator.id}-${c}`} className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground border border-border">{c}</span>
            ))}
          </div>
          <div className="col-span-1 hidden sm:block">
            <p className="text-[11px] text-muted-foreground">Followers</p>
            <p className="text-[13px] font-700 font-mono text-foreground">{creator.followersDisplay}</p>
          </div>
          <div className="col-span-1 hidden sm:block">
            <p className="text-[11px] text-muted-foreground">Engagement</p>
            <p className="text-[13px] font-700 font-mono text-positive">{creator.engagementRate}%</p>
          </div>
          <div className="col-span-1 hidden sm:flex items-center gap-1.5">
            <MomentumBadge score={creator.momentumScore} size="sm" />
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => setShortlisted(!shortlisted)}
            className={`p-1.5 rounded-md transition-all ${shortlisted ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
          >
            <Icon name="BookmarkIcon" size={14} />
          </button>
          <Link href="/creator-professional-page" className="flex items-center gap-1 bg-primary hover:bg-primary/90 text-primary-foreground text-[12px] font-600 px-2.5 py-1.5 rounded-md transition-all">
            Profil <Icon name="ArrowRightIcon" size={11} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border rounded-lg p-4 transition-all duration-200 ${
      selected ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/30'
    }`}>
      {/* Top row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <input
            type="checkbox"
            checked={selected}
            onChange={onSelect}
            className="w-3.5 h-3.5 accent-primary flex-shrink-0 mt-0.5"
          />
          <div className="story-ring-primary p-0.5 rounded-full">
            <div className="w-10 h-10 rounded-full overflow-hidden border border-background">
              <AppImage src={creator.avatar} alt={creator.avatarAlt} width={40} height={40} className="w-full h-full object-cover" />
            </div>
          </div>
          <div>
            <p className="text-[13px] font-700 text-foreground">{creator.name}</p>
            <p className="text-[11px] text-muted-foreground">{creator.handle}</p>
          </div>
        </div>
        <MomentumBadge score={creator.momentumScore} size="sm" />
      </div>

      {/* Status + availability */}
      <div className="flex items-center gap-1.5 mb-2.5 flex-wrap">
        {creator.verified && <StatusBadge variant="verified" size="sm" />}
        <span className={`text-[11px] px-1.5 py-0.5 rounded font-500 ${creator.available ? 'bg-positive/10 text-positive' : 'bg-muted text-muted-foreground'}`}>
          {creator.available ? '● Disponible' : '○ Occupé'}
        </span>
      </div>

      {/* Categories */}
      <div className="flex items-center gap-1 mb-2.5 flex-wrap">
        {creator.category.map(c => (
          <span key={`gcat-${creator.id}-${c}`} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-500 border border-primary/20">{c}</span>
        ))}
      </div>

      {/* Platforms + city */}
      <div className="flex items-center justify-between mb-3">
        <PlatformIcons platforms={creator.platforms} size="sm" />
        <span className="text-[11px] text-muted-foreground">{creator.city}</span>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        <div className="bg-muted/40 rounded-md p-2 text-center border border-border/50">
          <p className="text-[10px] text-muted-foreground">Followers</p>
          <p className="text-[13px] font-700 font-mono text-foreground">{creator.followersDisplay}</p>
        </div>
        <div className="bg-muted/40 rounded-md p-2 text-center border border-border/50">
          <p className="text-[10px] text-muted-foreground">Engagement</p>
          <p className="text-[13px] font-700 font-mono text-positive">{creator.engagementRate}%</p>
        </div>
        <div className="bg-muted/40 rounded-md p-2 text-center border border-border/50">
          <p className="text-[10px] text-muted-foreground">Audience DZ</p>
          <p className="text-[13px] font-700 font-mono text-foreground">{creator.audienceAlgeria}%</p>
        </div>
      </div>

      {/* Audience quality bar */}
      <div className="mb-3 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">Qualité audience</span>
          <span className="text-[10px] font-600 text-info">{creator.audienceQuality}/100</span>
        </div>
        <div className="h-1 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-primary to-info rounded-full" style={{ width: `${creator.audienceQuality}%` }} />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2.5 border-t border-border">
        <FitScoreBadge score={creator.fitScore} showLabel size="sm" />
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShortlisted(!shortlisted)}
            className={`p-1.5 rounded-md transition-all active:scale-95 ${shortlisted ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
            title="Ajouter à la shortlist"
          >
            <Icon name="BookmarkIcon" size={14} />
          </button>
          <Link
            href="/creator-professional-page"
            className="flex items-center gap-1 bg-primary hover:bg-primary/90 text-primary-foreground text-[12px] font-600 px-2.5 py-1.5 rounded-md transition-all active:scale-95"
          >
            Voir profil
            <Icon name="ArrowRightIcon" size={11} />
          </Link>
        </div>
      </div>
    </div>
  );
}